import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AgeVerification from '../components/AgeVerification';
import CookieConsent from '../components/CookieConsent';
import { Cookie, Settings, Eye, BarChart, Target, Shield } from 'lucide-react';

const CookiesPolicy = () => {
  return (
    <div className="min-h-screen bg-book-cream">
      <Header />
      <main className="book-section">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div className="w-16 h-16 bg-book-brown rounded-full flex items-center justify-center">
                <Cookie className="h-8 w-8 text-book-cream" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-book-brown mb-2">
                  Cookies Policy
                </h1>
                <p className="text-lg text-book-brown-dark">
                  Understanding how we use cookies to enhance your browsing experience
                </p>
              </div>
            </div>
            <div className="bg-book-beige rounded-lg p-4 text-sm text-book-brown-dark">
              <strong>Last Updated:</strong> 16 December 2025
            </div>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed mb-12">
            <p className="mb-6">
              Cookies represent essential tools for modern web experiences, enabling us to provide personalized,
              efficient, and secure services to our users. This Cookies Policy explains how Brit Ember uses cookies
              and similar technologies to enhance your experience while maintaining your privacy and complying with
              UK data protection regulations.
            </p>

            <p className="mb-6">
              Our cookie practices are designed to balance functionality with user control, ensuring that we can
              deliver high-quality services while respecting your privacy preferences. We provide clear information
              about each type of cookie we use and offer straightforward controls for managing your cookie preferences.
            </p>
          </div>

          {/* Main Content Sections */}
          <div className="space-y-12">
            {/* Section 1 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Cookie className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">What Are Cookies?</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      Cookies are small text files that are stored on your device when you visit websites. They serve
                      various purposes, from remembering your preferences to enabling essential website functionality.
                      Cookies help us provide a more personalized and efficient browsing experience.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">How Cookies Work</h3>
                    <p className="mb-4">
                      When you visit our website, cookies are created and stored on your device. On subsequent visits,
                      these cookies are read by our servers, allowing us to remember your preferences, maintain your
                      session, and provide personalized content. Cookies can be temporary (session cookies) or persistent
                      (stored until deleted or expired).
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Similar Technologies</h3>
                    <p className="mb-4">
                      In addition to cookies, we may use similar technologies such as web beacons, pixel tags, and
                      local storage. These technologies serve similar purposes and are subject to the same privacy
                      and control mechanisms outlined in this policy.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Settings className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Types of Cookies We Use</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      We categorize our cookies by purpose and functionality, ensuring transparency about how each
                      type contributes to your experience. Some cookies are essential for website operation, while
                      others enhance functionality and personalization.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Essential Cookies</h3>
                    <div className="bg-green-50 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-green-800 mb-2 flex items-center">
                        <Shield className="h-4 w-4 mr-2" />
                        Strictly Necessary
                      </h4>
                      <p className="text-green-700 text-sm mb-2">
                        These cookies are essential for the website to function properly and cannot be disabled.
                      </p>
                      <ul className="text-sm text-green-700 space-y-1">
                        <li>• Session management and security</li>
                        <li>• Age verification and access control</li>
                        <li>• Load balancing and server optimization</li>
                        <li>• Basic functionality and navigation</li>
                      </ul>
                    </div>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Performance Cookies</h3>
                    <div className="bg-blue-50 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
                        <BarChart className="h-4 w-4 mr-2" />
                        Analytics and Performance
                      </h4>
                      <p className="text-blue-700 text-sm mb-2">
                        These cookies help us understand how visitors use our website to improve performance and user experience.
                      </p>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Page load times and performance metrics</li>
                        <li>• Popular content and user journey analysis</li>
                        <li>• Error tracking and troubleshooting</li>
                        <li>• A/B testing and feature optimization</li>
                      </ul>
                    </div>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Functional Cookies</h3>
                    <div className="bg-purple-50 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-purple-800 mb-2 flex items-center">
                        <Settings className="h-4 w-4 mr-2" />
                        Preferences and Features
                      </h4>
                      <p className="text-purple-700 text-sm mb-2">
                        These cookies remember your choices and preferences to provide enhanced functionality.
                      </p>
                      <ul className="text-sm text-purple-700 space-y-1">
                        <li>• Language and location preferences</li>
                        <li>• Theme and display settings</li>
                        <li>• Form data preservation</li>
                        <li>• Personalized content delivery</li>
                      </ul>
                    </div>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Marketing Cookies</h3>
                    <div className="bg-orange-50 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-orange-800 mb-2 flex items-center">
                        <Target className="h-4 w-4 mr-2" />
                        Advertising and Marketing
                      </h4>
                      <p className="text-orange-700 text-sm mb-2">
                        These cookies are used to deliver relevant advertisements and track campaign effectiveness.
                      </p>
                      <ul className="text-sm text-orange-700 space-y-1">
                        <li>• Interest-based advertising</li>
                        <li>• Campaign performance measurement</li>
                        <li>• Affiliate marketing tracking</li>
                        <li>• Cross-platform advertising</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Eye className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Third-Party Cookies</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      Some cookies on our website are set by third-party services that appear on our pages. We carefully
                      select these partners to ensure they meet our privacy and security standards. Third-party cookies
                      enable enhanced functionality and services while maintaining user privacy.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Analytics Providers</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li><strong>Google Analytics:</strong> Website usage analysis and performance optimization</li>
                      <li><strong>Hotjar:</strong> User experience insights and heat mapping</li>
                      <li><strong>Crazy Egg:</strong> User behavior tracking and conversion optimization</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Customer Support</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li><strong>Intercom:</strong> Live chat functionality and user communication</li>
                      <li><strong>Zendesk:</strong> Support ticket management and knowledge base</li>
                      <li><strong>Help Scout:</strong> Customer service and ticketing system</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Content Delivery</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li><strong>Cloudflare:</strong> Content delivery network and security</li>
                      <li><strong>Amazon Web Services:</strong> Cloud infrastructure and data storage</li>
                      <li><strong>Google Fonts:</strong> Web typography and font delivery</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Social Media Integration</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li><strong>Facebook Pixel:</strong> Social media advertising and audience insights</li>
                      <li><strong>Twitter Pixel:</strong> Social media tracking and campaign measurement</li>
                      <li><strong>LinkedIn Insight:</strong> Professional network advertising and analytics</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Settings className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Managing Your Cookie Preferences</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      We provide multiple options for managing your cookie preferences, ensuring you maintain control
                      over your privacy while using our services. You can customize your cookie settings at any time
                      through various methods designed for user convenience.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Cookie Consent Banner</h3>
                    <p className="mb-4">
                      When you first visit our website, you'll see a cookie consent banner that allows you to accept
                      all cookies, reject non-essential cookies, or customize your preferences. Your choices are saved
                      and applied across all your visits to our website.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Browser Settings</h3>
                    <p className="mb-4">
                      Most web browsers allow you to control cookies through their settings preferences. You can typically:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>View what cookies are stored on your device</li>
                      <li>Delete existing cookies</li>
                      <li>Block cookies from specific websites</li>
                      <li>Block all cookies from being placed</li>
                      <li>Clear cookies when you close your browser</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Device-Specific Controls</h3>
                    <p className="mb-4">
                      Mobile devices and tablets also provide cookie management options through their operating systems.
                      You can access these controls through your device's privacy settings to manage app permissions
                      and tracking preferences.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Opt-Out Links</h3>
                    <p className="mb-4">
                      For specific third-party services, you can opt out directly through their privacy policies:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li><a href="https://tools.google.com/dlpage/gaoptout" className="text-book-brown hover:text-book-brown-light underline">Google Analytics Opt-out</a></li>
                      <li><a href="https://www.hotjar.com/legal/compliance/opt-out" className="text-book-brown hover:text-book-brown-light underline">Hotjar Opt-out</a></li>
                      <li><a href="https://www.facebook.com/ads/preferences" className="text-book-brown hover:text-book-brown-light underline">Facebook Advertising Preferences</a></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Shield className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Cookie Security and Updates</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      Cookie security represents a critical component of our overall data protection strategy. We implement
                      multiple security measures to ensure that cookie data is handled safely and in compliance with
                      UK data protection regulations.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Security Measures</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>Secure HTTPS transmission for all cookie data</li>
                      <li>Encrypted storage of sensitive cookie information</li>
                      <li>Regular security audits and cookie vulnerability assessments</li>
                      <li>Compliance with PCI DSS standards for payment-related cookies</li>
                      <li>Automatic expiration and cleanup of outdated cookies</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Policy Updates</h3>
                    <p className="mb-4">
                      We regularly review and update our Cookies Policy to reflect changes in technology, regulations,
                      and industry best practices. When we make material changes to this policy, we will notify users
                      through our website and may require re-consent for certain cookie categories.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Contact Information</h3>
                    <p className="mb-4">
                      If you have any questions about our Cookies Policy or need assistance managing your cookie preferences,
                      please contact our privacy team using the information provided below.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-book-beige rounded-xl p-8 shadow-book-inset mt-12">
            <h2 className="text-2xl font-bold text-book-brown mb-6 text-center">Cookie Policy Questions?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center">
                <h3 className="font-semibold text-book-brown mb-2">Privacy Team</h3>
                <p className="text-book-brown-dark mb-1">privacy@britember.com</p>
                <p className="text-sm text-book-brown-dark">For cookie and privacy inquiries</p>
              </div>
              <div className="text-center">
                <h3 className="font-semibold text-book-brown mb-2">Technical Support</h3>
                <p className="text-book-brown-dark mb-1">support@britember.com</p>
                <p className="text-sm text-book-brown-dark">For technical cookie issues</p>
              </div>
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-book-brown-dark">
                <strong>Cookie Preferences:</strong> You can manage your cookie settings at any time through our website preferences.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <AgeVerification />
      <CookieConsent />
    </div>
  );
};

export default CookiesPolicy;
